import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { fetchProjectUsers } from "./taskService";
import SubProjectFileAttachments from "./SubProjectFileAttachments";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";

const VISIBILITY_OPTIONS = ["Private", "Public", "Team"];

const normalizeDateInput = (v) => {
  if (v == null || v === "") return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

function EditSubproject({ onBack, onUpdateSubproject }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId } = useParams();
  const role = useMemo(() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canEditSubprojectFiles = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    subProjectName: "",
    shortCode: "",
    description: "",
    domain: "",
    category: "",
    startDate: "",
    endDate: "",
    actualStartDate: "",
    actualEndDate: "",
    visibility: "Private",
    statusId: "",
    userIds: []
  });
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  useEffect(() => {
    const fetchSubproject = async () => {
      if (!subprojectId || !projectId) return;
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/sub-projects/${subprojectId}`);
        const raw = response.data?.data ?? response.data;
        const sp = raw && (raw.Sub_Project_Id != null || raw.sub_project_id != null) ? raw : null;
        if (sp) {
          let cust = sp.client_id ?? sp.client_id ?? null;
          if (cust == null) {
            try {
              const pr = await axiosInstance.get(`/projects/${projectId}`);
              const p = pr.data?.data ?? pr.data;
              cust = p?.client_id ?? p?.client_id ?? null;
            } catch (e) {
              console.error("Could not resolve client_id for subproject:", e);
            }
          }
          setCustomerId(cust != null ? Number(cust) : null);
          setFormData({
            subProjectName: sp.Sub_Project_Name || sp.sub_project_name || "",
            shortCode: sp.Short_Code || sp.short_code || "",
            description: sp.Description || sp.description || "",
            domain: (sp.Domain_Id ?? sp.domain_id ?? "") ? String(sp.Domain_Id ?? sp.domain_id) : "",
            category: (sp.Category_Id ?? sp.category_id ?? "") ? String(sp.Category_Id ?? sp.category_id) : "",
            startDate: normalizeDateInput(sp.Start_Date || sp.start_date),
            endDate: normalizeDateInput(sp.End_Date || sp.end_date),
            actualStartDate: normalizeDateInput(sp.Actual_Start_Date || sp.actual_start_date),
            actualEndDate: normalizeDateInput(sp.Actual_End_Date || sp.actual_end_date),
            visibility: sp.Visibility || sp.visibility || "Private",
            statusId: (sp.Status_Id ?? sp.status_id ?? sp.StatusId ?? sp.statusId) ? String(sp.Status_Id ?? sp.status_id ?? sp.StatusId ?? sp.statusId) : "",
            userIds: (sp.Users || sp.users || []).map((u) => u.User_Id || u.user_id).filter(Boolean)
          });
        } else {
          setError("Subproject not found.");
        }
      } catch (err) {
        console.error("Error fetching subproject:", err);
        setError(err.response?.data?.message || "Failed to load subproject.");
      } finally {
        setLoading(false);
      }
    };
    fetchSubproject();
  }, [subprojectId, projectId]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!projectId) {
        setLoadingUsers(false);
        return;
      }
      try {
        const projectUsers = await fetchProjectUsers(projectId, subprojectId);
        const mapped = projectUsers.map((u) => ({
          id: u.User_Id ?? u.user_id ?? u.id,
          name: (u.User_Name ?? u.user_name) || "—"
        })).filter((x) => x.id);
        setUsers(mapped);
      } catch (err) {
        console.error("Error fetching project users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    const fetchDomains = async () => {
      try {
        const response = await axiosInstance.get("/domains");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setDomains(arr.map((d) => ({ id: d.Domain_Id || d.domain_id || d.id, name: d.Domain_Name || d.domain_name || d.name || "—" })).filter((x) => x.id));
      } catch (err) {
        console.error("Error fetching domains:", err);
      } finally {
        setLoadingDomains(false);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/project-categories");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setCategories(arr.map((c) => ({ id: c.Category_Id || c.category_id || c.id, name: c.Category_Name || c.category_name || c.name || "—" })).filter((x) => x.id));
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    };
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/project-statuses");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const mapped = arr
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? s.name ?? "—"
          }))
          .filter((x) => x.id)
          .sort((a, b) => a.id - b.id);
        setStatuses(mapped);
      } catch (err) {
        console.error("Error fetching project statuses:", err);
      } finally {
        setLoadingStatuses(false);
      }
    };
    loadUsers();
    fetchDomains();
    fetchCategories();
    fetchStatuses();
  }, [projectId, subprojectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.userIds.includes(userId) ? prev.userIds.filter((id) => id !== userId) : [...prev.userIds, userId];
      return { ...prev, userIds: ids };
    });
  };

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.userIds.length === users.length;

  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => {
      if (allUsersSelected) {
        return { ...prev, userIds: [] };
      }
      return { ...prev, userIds: users.map((u) => u.id) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subProjectName || !formData.shortCode || !projectId || !subprojectId) {
      alert("Please fill in required fields.");
      return;
    }
    let updatedBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.User_Id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    if (
      formData.actualStartDate &&
      formData.actualEndDate &&
      formData.actualEndDate < formData.actualStartDate
    ) {
      alert("Actual end date cannot be before actual start date.");
      return;
    }
    const payload = {
      client_id: customerId != null ? Number(customerId) : undefined,
      Project_Id: Number(projectId),
      Sub_Project_Name: formData.subProjectName.trim(),
      Short_Code: formData.shortCode.trim(),
      Description: formData.description?.trim() || null,
      Domain_Id: formData.domain ? Number(formData.domain) : null,
      Category_Id: formData.category ? Number(formData.category) : null,
      Visibility: formData.visibility || "Private",
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Actual_Start_Date: formData.actualStartDate || null,
      Actual_End_Date: formData.actualEndDate || null,
      Updated_By: Number(updatedBy),
      User_Ids: formData.userIds.map((id) => Number(id)),
      Status_Id: formData.statusId ? parseInt(formData.statusId, 10) : null
    };
    setSubmitting(true);
    try {
      await axiosInstance.put(`/sub-projects/${subprojectId}`, payload);
      alert("Subproject updated successfully");
      if (onUpdateSubproject) onUpdateSubproject();
      if (onBack) onBack();
      else navigate(`/projects/${projectId}/view`);
    } catch (err) {
      console.error("Error updating subproject:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to update subproject.");
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else navigate(`/projects/${projectId}/view`);
  };

  if (loading) {
    return (
      <div className="project-form-container editcustomer-container">
        <div className="editcustomer-loading">Loading subproject...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-form-container editcustomer-container">
        <button type="button" className="viewlead-back-link" onClick={() => navigate(`/projects/${projectId}/view`)}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message" style={{ marginTop: 16 }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="project-form-container editcustomer-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <form className="editcustomer-form" onSubmit={handleSubmit}>
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            flexWrap: "nowrap",
            marginBottom: 20
          }}
        >
          <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
            <div className="form-section">
              <div className="form-grid">
                <div className="form-group">
                  <label>Subproject Name <span className="required">*</span></label>
                  <input type="text" name="subProjectName" value={formData.subProjectName} onChange={handleInputChange} required  />
                </div>
                <div className="form-group">
                  <label>Short Code <span className="required">*</span></label>
                  <input type="text" name="shortCode" value={formData.shortCode} onChange={handleInputChange} required  maxLength={20} />
                </div>
                <div className="form-group">
                  <label>Domain</label>
                  <select name="domain" value={formData.domain} onChange={handleInputChange} disabled={loadingDomains}>
                    <option value="">{loadingDomains ? "Loading domains..." : "Select Domain"}</option>
                    {domains.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} disabled={loadingCategories}>
                    <option value="">{loadingCategories ? "Loading categories..." : "Select Category"}</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="statusId"
                    value={formData.statusId}
                    onChange={handleInputChange}
                    disabled={loadingStatuses}
                  >
                    <option value="">{loadingStatuses ? "Loading statuses..." : "Select Status"}</option>
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Visibility</label>
                  <select name="visibility" value={formData.visibility} onChange={handleInputChange}>
                    {VISIBILITY_OPTIONS.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" name="endDate" value={formData.endDate} onChange={handleInputChange} min={formData.startDate || undefined} />
                </div>
                <div className="form-group">
                  <label>Actual Start Date</label>
                  <input type="date" name="actualStartDate" value={formData.actualStartDate} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Actual End Date</label>
                  <input
                    type="date"
                    name="actualEndDate"
                    value={formData.actualEndDate}
                    onChange={handleInputChange}
                    min={formData.actualStartDate || undefined}
                  />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label>Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4"  />
              </div>
            </div>
          </div>

          <div className="viewlead-card" style={{ flex: "1 1 0", minWidth: 260, maxWidth: 420 }}>
            <div className="form-section">
              <h3 className="viewlead-section-title" style={{ marginTop: 0 }}>Team Members</h3>
              <div className="form-group form-group-multi-select" style={{ marginTop: 8 }}>
                <div style={{ marginTop: 0, maxWidth: 320, width: "100%", alignSelf: "flex-start" }}>
                  <div
                    className="permissions-select-trigger"
                    onClick={() => {
                      if (!loadingUsers) setShowTeamDropdown((prev) => !prev);
                    }}
                    style={{
                      opacity: loadingUsers ? 0.6 : 1,
                      cursor: loadingUsers ? "default" : "pointer"
                    }}
                  >
                    <span className="permissions-select-text">
                      {loadingUsers
                        ? "Loading team members..."
                        : formData.userIds.length === 0
                          ? "Select team members"
                          : `${formData.userIds.length} member${formData.userIds.length > 1 ? "s" : ""} selected`}
                    </span>
                    <FaChevronDown
                      className={`permissions-select-chevron ${showTeamDropdown ? "open" : ""}`}
                    />
                  </div>
                  {showTeamDropdown && !loadingUsers && (
                    <div
                      className="permissions-select-panel"
                      style={{ maxHeight: 220, overflowY: "auto" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="permissions-select-item">
                        <input
                          type="checkbox"
                          checked={allUsersSelected}
                          onChange={handleToggleSelectAllUsers}
                        />
                        <span className="permissions-select-item-label">Select all</span>
                      </label>
                      {users.map((u) => (
                        <label
                          key={u.id}
                          className="permissions-select-item"
                        >
                          <input
                            type="checkbox"
                            checked={formData.userIds.includes(u.id)}
                            onChange={() => handleUserToggle(u.id)}
                          />
                          <span className="permissions-select-item-label">{u.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {subprojectId ? (
          <div
            className="viewlead-card issue-attachments-card project-attachments-card"
            style={{ marginTop: 20, marginBottom: 8 }}
          >
            <SubProjectFileAttachments
              subproject={{ Sub_Project_Id: Number(subprojectId), sub_project_id: Number(subprojectId) }}
              subprojectId={subprojectId}
              canUpload={canEditSubprojectFiles}
              title="Attachments"
              onRefresh={async () => {
                if (!subprojectId) return;
                try {
                  await axiosInstance.get(`/sub-projects/${subprojectId}`);
                } catch (err) {
                  console.error("Error refetching subproject after file change:", err);
                }
              }}
            />
          </div>
        ) : null}

        <div
          className="form-actions editcustomer-actions"
          style={{ display: "inline-flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "center", gap: 10 }}
        >
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Updating..." : "Update Subproject"}
          </button>
          <button type="button" className="btn-cancel" onClick={goBack}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditSubproject;
